import React, { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import TechnicalLogModal from '../communications/TechnicalLogModal';
import { normalizeOutboundPhoneAddress } from '../../lib/communicationsCompose';
import { formatPhone } from '../../lib/phone';

const SESSION_COLUMNS = 'id, channel, contact_phone, contact_email, account_id, created_at, last_inbound_at, last_outbound_at, conversation_log, raw_message_log';

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

function activityTime(session) {
    const stamps = [session?.last_inbound_at, session?.last_outbound_at, session?.created_at]
        .map((value) => (value ? new Date(value).getTime() : 0))
        .filter((value) => Number.isFinite(value) && value > 0);
    return stamps.length ? Math.max(...stamps) : 0;
}

function formatActivity(session) {
    const stamp = activityTime(session);
    if (!stamp) return 'No activity yet';
    return new Date(stamp).toLocaleString();
}

function sessionSearchText(row) {
    return [
        row.name,
        row.session?.contact_phone,
        formatPhone(row.session?.contact_phone),
        row.session?.contact_email,
        row.session?.channel,
    ].filter(Boolean).join(' ').toLowerCase();
}

function normalizePhone(value) {
    return normalizeOutboundPhoneAddress(value).replace(/\D/g, '');
}

function ReportsLogsSettingsView({ theme = 'light' }) {
    const dark = theme === 'dark';
    const [sessions, setSessions] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        Promise.all([
            supabaseClient.from('intake_sessions')
                .select(SESSION_COLUMNS)
                .order('last_inbound_at', { ascending: false, nullsFirst: false }),
            supabaseClient.from('accounts').select('id, account_name').order('account_name'),
            supabaseClient.from('contacts').select('id, name, phones, emails, primary_account_id').order('name'),
        ]).then(([sessionsResult, accountsResult, contactsResult]) => {
            if (!alive) return;
            if (sessionsResult.error) {
                setError(sessionsResult.error.message || 'Could not load technical logs.');
                setSessions([]);
            } else {
                setSessions(sessionsResult.data || []);
            }
            if (!accountsResult.error) setAccounts(accountsResult.data || []);
            if (!contactsResult.error) setContacts(contactsResult.data || []);
        }).catch((loadError) => {
            if (alive) setError(loadError.message || 'Could not load technical logs.');
        }).finally(() => {
            if (alive) setLoading(false);
        });
        return () => { alive = false; };
    }, []);

    const accountById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
    const rows = useMemo(() => {
        const contactsByAccount = new Map();
        const contactsByPhone = new Map();
        const contactsByEmail = new Map();
        contacts.forEach((contact) => {
            if (contact.primary_account_id && !contactsByAccount.has(contact.primary_account_id)) contactsByAccount.set(contact.primary_account_id, contact);
            (Array.isArray(contact.phones) ? contact.phones : []).forEach((phone) => {
                const key = normalizePhone(phone);
                if (key && !contactsByPhone.has(key)) contactsByPhone.set(key, contact);
            });
            (Array.isArray(contact.emails) ? contact.emails : []).forEach((email) => {
                const key = firstString(email).toLowerCase();
                if (key && !contactsByEmail.has(key)) contactsByEmail.set(key, contact);
            });
        });
        return sessions
            .map((session) => {
                const contact = contactsByPhone.get(normalizePhone(session.contact_phone))
                    || contactsByEmail.get(firstString(session.contact_email).toLowerCase())
                    || contactsByAccount.get(session.account_id);
                const account = accountById.get(session.account_id);
                const name = firstString(contact?.name, account?.account_name, session.contact_email, formatPhone(session.contact_phone)) || 'Unknown contact';
                return { session, contact, account, name, activityAt: activityTime(session) };
            })
            .sort((a, b) => b.activityAt - a.activityAt);
    }, [accountById, contacts, sessions]);

    const visibleRows = useMemo(() => {
        const needles = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (!needles.length) return rows;
        return rows.filter((row) => {
            const haystack = sessionSearchText(row);
            return needles.every((needle) => haystack.includes(needle));
        });
    }, [rows, search]);

    const panelCls = dark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-800';
    const labelCls = dark ? 'text-gray-400' : 'text-gray-500';
    const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${dark
        ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500'
        : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'}`;
    const rowCls = `w-full rounded-lg border p-3 text-left transition ${dark ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`;

    return (
        <div className={`ia-settings-reports mx-auto max-w-5xl rounded-xl border p-4 ${panelCls}`}>
            <div className="mb-4">
                <h2 className="text-lg font-semibold">Reports & Logs</h2>
                <p className={`text-sm ${labelCls}`}>Technical send/receive logs, per conversation.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-[20rem,1fr]">
                <section className="min-h-[24rem]">
                    <input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search name, phone, email or channel…"
                        aria-label="Search technical logs"
                        className={inputCls}
                    />
                    <div className="mt-3 max-h-[32rem] space-y-2 overflow-auto">
                        {loading ? (
                            <div className={`p-4 text-sm ${labelCls}`}>Loading logs…</div>
                        ) : error ? (
                            <div className="rounded-lg border border-[var(--ui-alert)]/30 bg-[var(--ui-alert)]/10 p-3 text-sm text-[var(--ui-alert)]">Couldn’t load logs: {error}</div>
                        ) : visibleRows.length === 0 ? (
                            <div className={`p-4 text-sm ${labelCls}`}>{rows.length === 0 ? 'No conversations yet.' : 'No conversations match that search.'}</div>
                        ) : visibleRows.map((row) => (
                            <button
                                key={row.session.id}
                                type="button"
                                onClick={() => setSelectedSession(row.session)}
                                className={`${rowCls} ${selectedSession?.id === row.session.id ? 'ring-2 ring-[var(--accent-ink)]' : ''}`}
                            >
                                <div className="truncate text-sm font-medium">{row.name}</div>
                                <div className={`mt-0.5 truncate text-xs ${labelCls}`}>
                                    {[formatPhone(row.session.contact_phone), row.session.contact_email, row.session.channel?.toUpperCase()].filter(Boolean).join(' · ') || 'No contact address'}
                                </div>
                                <div className={`mt-1 text-[11px] ${labelCls}`}>{formatActivity(row.session)}</div>
                            </button>
                        ))}
                    </div>
                </section>
                <section className={`rounded-xl border p-6 ${dark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                    <h3 className="text-base font-semibold">Technical log viewer</h3>
                    <p className={`mt-1 text-sm ${labelCls}`}>
                        Select a conversation on the left to open its raw AI Attendant and provider audit trail.
                    </p>
                    {selectedSession && (
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={() => setSelectedSession(selectedSession)}
                                className="rounded-lg bg-[var(--accent-ink)] px-3 py-2 text-sm font-medium text-white"
                            >
                                Reopen selected technical log
                            </button>
                        </div>
                    )}
                </section>
            </div>
            {selectedSession && (
                <TechnicalLogModal intakeSession={selectedSession} onClose={() => setSelectedSession(null)} theme={theme} />
            )}
        </div>
    );
}

export default ReportsLogsSettingsView;
